import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { ApiError } from "../../../Common/ApiError";
import { PanelLoading } from "../../../Common/LoadingPlaceholder";
import HintTooltip from "../../../Common/HintTooltip";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import { formatDateTime } from "../../../../../utils/DateUtils";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import { describeDrawerDifference, isIntegrationCash, resolveCashOwner } from "./cashPresentation";
import "./CashDetail.css";

// O detalhe mostra só as últimas movimentações: serve para conferir o fim do turno, não para
// auditar o caixa inteiro (o automático passa de dez mil). A lista completa fica em Movimentações.
const LATEST_TRANSACTIONS = 10;

/**
 * Tempo entre a abertura e o fechamento (ou agora, no caixa aberto), em horas e minutos.
 * Acima de um dia mostra os dias, porque caixa esquecido aberto é justamente o que se quer ver.
 */
export const formatCashDuration = (start, end = new Date()) => {
    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;
    if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return "-";
    }
    const totalMinutes = Math.max(0, Math.floor((endDate - startDate) / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) {
        return `${days}d ${hours}h`;
    }
    return hours > 0 ? `${hours}h ${String(minutes).padStart(2, "0")}min` : `${minutes}min`;
};

const money = (t, value) => `${t("currency_symbol")} ${convertDoubleToStringPTBR(value || 0)}`;

/**
 * Lista de um agrupamento (forma de pagamento ou origem): entradas e saídas de cada grupo e
 * quantas movimentações ele teve.
 *
 * @param {{title: string, lines: Array, labelOf: function}} props
 */
const BreakdownList = ({ title, lines, labelOf }) => {
    const { t } = useTranslation();
    return (
        <section className="cash-detail__breakdown">
            <h6>{title}</h6>
            {lines.length === 0 ? (
                <p className="cash-detail__muted mb-0">{t("cash_flow.no_transactions")}</p>
            ) : (
                <ul>
                    {lines.map((line) => (
                        <li key={line.key}>
                            <div>
                                <strong>{labelOf(line.key)}</strong>
                                <small>{line.count} {line.count === 1 ? t("cash_flow.transaction_singular") : t("cash_flow.transaction_plural")}</small>
                            </div>
                            <div className="cash-detail__breakdown-values">
                                {line.incomes > 0 && <span className="cash-detail__income">+ {money(t, line.incomes)}</span>}
                                {line.expenses > 0 && <span className="cash-detail__expense">- {money(t, line.expenses)}</span>}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

/**
 * Detalhe do caixa na tela de Caixas.
 *
 * Antes mostrava só os dados de abertura e fechamento; para conferir o caixa era preciso ir à
 * tela de Movimentações e somar à mão. Agora traz os totais, quanto dinheiro deveria estar na
 * gaveta e de onde veio cada valor, com as movimentações do próprio caixa. Os totais vêm do
 * backend (CashService.summarize) com o mesmo recorte do fechamento.
 *
 * @param {{cash: object}} props caixa da listagem
 */
const CashDetail = ({ cash }) => {
    const { t } = useTranslation();
    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [transactionsTotal, setTransactionsTotal] = useState(0);
    const [loadingTransactions, setLoadingTransactions] = useState(false);

    useEffect(() => {
        if (!cash?.id) {
            return undefined;
        }
        // A listagem monta o detalhe com key pelo id do caixa, então trocar de caixa recomeça o
        // estado; o active só descarta a resposta que chegar depois de o modal fechar.
        let active = true;
        setLoadingSummary(true);
        axiosInstance.get(`${Api.cash_flow.cash}/${cash.id}/summary`)
            .then(({ data }) => {
                if (active) setSummary(data);
            })
            .catch((error) => {
                if (active) ApiError(error);
            })
            .finally(() => {
                if (active) setLoadingSummary(false);
            });
        return () => {
            active = false;
        };
    }, [cash?.id]);

    useEffect(() => {
        if (!cash?.id) {
            return undefined;
        }
        let active = true;
        setLoadingTransactions(true);
        axiosInstance.post(`${Api.cash_flow.transaction}/pageable`, {
            filters: [{ field: "cash.id", value: cash.id, operation: SearchOperation.EQUAL }],
            page: 0,
            size: LATEST_TRANSACTIONS,
            orders: [{ field: "transactionDate", direction: "DESC" }]
        })
            .then(({ data }) => {
                if (!active) return;
                setTransactions(data.content || []);
                setTransactionsTotal(data.totalElements || 0);
            })
            .catch((error) => {
                if (active) ApiError(error);
            })
            .finally(() => {
                if (active) setLoadingTransactions(false);
            });
        return () => {
            active = false;
        };
    }, [cash?.id]);

    if (!cash) {
        return null;
    }

    const automatic = isIntegrationCash(cash);
    const closed = Boolean(cash.closingDate);
    const openedAt = cash.openingDate || cash.createdAt;
    const statusLabel = automatic && !closed ? t("cash_flow.active") : closed ? t("cash_flow.closed") : t("cash_flow.open");
    const statusVariant = automatic && !closed ? "info" : closed ? "danger" : "success";
    const paymentLabel = (key) => t(String(key).toLowerCase(), { defaultValue: key });
    const originLabel = (key) => t(`cash_flow.origin_${String(key).toLowerCase()}`, { defaultValue: key });
    const drawerSituation = describeDrawerDifference(summary?.drawerDifference);

    return (
        <div className="cash-detail">
            <div className={`cash-detail__hero cash-detail__hero--${statusVariant}`}>
                <div>
                    <span className="cash-detail__kicker">{t("cash_flow.cash")}</span>
                    <h5 className="mb-1">{resolveCashOwner(cash, t)}</h5>
                    <span className="cash-detail__muted">{cash.branch?.name}</span>
                </div>
                <Badge bg={statusVariant} className="cash-detail__badge">{statusLabel}</Badge>
            </div>

            <div className="cash-detail__meta">
                <div>
                    <span>{t("cash_flow.opened_at")}</span>
                    <strong>{formatDateTime(openedAt)}</strong>
                </div>
                <div>
                    <span>{closed ? t("cash_flow.closed_at") : t("cash_flow.open_for")}</span>
                    <strong>{closed ? formatDateTime(cash.closingDate) : formatCashDuration(openedAt)}</strong>
                </div>
                {closed && (
                    <div>
                        <span>{t("cash_flow.closed_by")}</span>
                        <strong>{cash.closingUser?.fullName || "-"}</strong>
                    </div>
                )}
                {closed && (
                    <div>
                        <span>{t("duration")}</span>
                        <strong>{formatCashDuration(openedAt, cash.closingDate)}</strong>
                    </div>
                )}
                {cash.obs && (
                    <div className="cash-detail__meta-wide">
                        <span>{t("cash_flow.opening_obs")}</span>
                        <strong>{cash.obs}</strong>
                    </div>
                )}
                {cash.closingObs && (
                    <div className="cash-detail__meta-wide">
                        <span>{t("cash_flow.closing_obs")}</span>
                        <strong>{cash.closingObs}</strong>
                    </div>
                )}
            </div>

            {loadingSummary || !summary ? (
                <PanelLoading height={220} label={t("cash_flow.calculating_cash_summary")} />
            ) : (
                <>
                    <div className="cash-detail__totals">
                        <div>
                            <span>{t("cash_flow.opening_balance")}</span>
                            <strong>{money(t, summary.openingBalance)}</strong>
                        </div>
                        <div>
                            <span>{t("cash_flow.incomes")}</span>
                            <strong className="cash-detail__income">{money(t, summary.totalIncomes)}</strong>
                        </div>
                        <div>
                            <span>{t("cash_flow.expenses")}</span>
                            <strong className="cash-detail__expense">{money(t, summary.totalExpenses)}</strong>
                        </div>
                        <div>
                            <span>
                                {t("cash_flow.final_balance")}
                                <HintTooltip text={t("cash_flow.final_balance_hint")} />
                            </span>
                            <strong>{money(t, summary.closingBalance)}</strong>
                        </div>
                    </div>

                    {/* O caixa automático não tem gaveta: só o de balcão mostra o valor para conferir. */}
                    {summary.expectedDrawerCash != null && (
                        <div className="cash-detail__drawer">
                            <i className="fas fa-cash-register" aria-hidden="true"></i>
                            <div>
                                <span>{t("cash_flow.expected_drawer_cash")}</span>
                                <strong>{money(t, summary.expectedDrawerCash)}</strong>
                                <small>{t("cash_flow.expected_drawer_cash_hint")}</small>
                            </div>
                            {/* Contagem informada no fechamento, com a falta ou a sobra. */}
                            {summary.countedCash != null && (
                                <div className="cash-detail__counted">
                                    <span>{t("cash_flow.counted_cash")}</span>
                                    <strong>{money(t, summary.countedCash)}</strong>
                                    {drawerSituation && (
                                        <small className={`cash-detail__difference cash-detail__difference--${drawerSituation.tone}`}>
                                            {t(drawerSituation.key, { amount: money(t, drawerSituation.amount) })}
                                        </small>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="cash-detail__breakdowns">
                        <BreakdownList title={t("cash_flow.by_payment_method")} lines={summary.byPaymentMethod || []} labelOf={paymentLabel} />
                        <BreakdownList title={t("cash_flow.by_origin")} lines={summary.byOrigin || []} labelOf={originLabel} />
                    </div>
                </>
            )}

            <section className="cash-detail__transactions">
                <div className="cash-detail__transactions-head">
                    <h6>{t("cash_flow.latest_transactions")}</h6>
                    {transactionsTotal > LATEST_TRANSACTIONS && (
                        <small className="cash-detail__muted">
                            {t("cash_flow.latest_transactions_of_total", { shown: LATEST_TRANSACTIONS, total: transactionsTotal })}
                        </small>
                    )}
                </div>
                {transactions.length === 0 && !loadingTransactions ? (
                    <p className="cash-detail__muted mb-0">{t("cash_flow.no_transactions")}</p>
                ) : (
                    <ul>
                        {transactions.map((transaction) => {
                            const income = transaction.type === "IN";
                            return (
                                <li key={transaction.id}>
                                    <div className="cash-detail__transaction-main">
                                        <strong>{transaction.description || "-"}</strong>
                                        <small>
                                            {formatDateTime(transaction.transactionDate || transaction.createdAt)}
                                            {transaction.paymentMethod && <>, {paymentLabel(transaction.paymentMethod)}</>}
                                        </small>
                                    </div>
                                    {transaction.origin && transaction.origin !== "MANUAL" && (
                                        <Badge bg="light" text="dark" className="cash-detail__origin">{originLabel(transaction.origin)}</Badge>
                                    )}
                                    <strong className={income ? "cash-detail__income" : "cash-detail__expense"}>
                                        {income ? "+" : "-"} {money(t, transaction.value)}
                                    </strong>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
};

export default CashDetail;
